import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Bell, ChevronRight } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

import { cn } from "@/shared/lib/utils";
import { useAppSelector } from "@/app/store";
import {
  selectIsMobile,
} from "@/app/store/slices/ui.slice";
import { selectUser, selectUserPermissions } from "@/app/store/slices/auth.slice";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { CurrencySelector } from "./CurrencySelector";
import {
  navigationConfig,
  filterSectionsByPermissions,
  isBranchAccountUser,
  isTransfersNavPath,
} from "@/app/layout/config/navigation.config";
import { IconCentralKitchen } from "@/assets/svgs/iconCentralKitchen";

export function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const user = useAppSelector(selectUser);
  const permissions = useAppSelector(selectUserPermissions);
  const isMobile = useAppSelector(selectIsMobile);
  const filteredSections = useMemo(
    () =>
      filterSectionsByPermissions(
        navigationConfig,
        permissions,
        isBranchAccountUser(user),
      ),
    [permissions, user],
  );

  // Map parent segment to detail page label key
  const detailLabelMap: Record<string, string> = {
    suppliers: t("suppliers.supplierDetails"),
    items: t("items.itemDetails"),
    "purchase-orders": t("purchaseOrders.purchaseOrderDetails"),
    "requests-sent": t("requestsSent.details.title"),
    "prepared-item-requests": t("preparedItemRequests.details.title"),
    inventory: t("inventory.itemStockDetails"),
  };

  const pathSegmentLabelKeys: Record<string, string> = {
    dashboard: "nav.dashboard",
    inventory: "nav.inventory",
    "purchase-orders": "nav.purchaseOrders",
    "receiving-qc": "nav.receivingQc",
    items: "nav.items",
    "raw-materials": "nav.rawMaterials",
    preparedItems: "nav.preparedItems",
    suppliers: "nav.suppliers",
    profile: "nav.profile",
    settings: "nav.settings",
    transfers: "nav.transfers",
    "incoming-transfers": "nav.incomingTransfers",
    "incoming-requests": "nav.incomingRequests",
    "requests-sent": "nav.requestsSent",
    "prepared-item-requests": "nav.transfersManagement",
    "aggregated-item-details": "preparedItemRequests.details.aggregatedTitle",
    "branch-transfers": "nav.transfersManagement",
    "preparation-management": "nav.preparationManagement",
    "produced-output": "nav.producedOutput",
  };

  const getContextLabel = () => {
    if (user?.branchName) return `${t("nav.branch")} ${user.branchName}`;
    return t("nav.centralKitchen");
  };

  const getPathSegmentLabel = (segment: string) => {
    const labelKey = pathSegmentLabelKeys[segment];
    if (labelKey) return t(labelKey);
    return segment
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // const getNavBreadcrumbs = () => {
  //   for (const section of filteredSections) {
  //     if (!section.breadcrumbParentLabelKey) continue;

  //     for (const item of section.items) {
  //       debugger;
  //       const isTransfersItem =
  //         item.id === "transfers" && isTransfersNavPath(location.pathname);
  //       const isOnItemRoute =
  //         item.href != null && location.pathname.startsWith(item.href);

  //       if (isOnItemRoute || isTransfersItem) {
  //         console.log("testtttt", section.breadcrumbParentLabelKey)
  //         return [
  //           {
  //             label: t(section.breadcrumbParentLabelKey),
  //             path: undefined,
  //             showKitchenIcon:
  //               section.breadcrumbParentLabelKey === "nav.centralKitchen",
  //           },
  //           {
  //             label: t(item.breadcrumbLabelKey ?? item.labelKey),
  //             path: item.href,
  //             showKitchenIcon: false,
  //           },
  //         ];
  //       }
  //     }
  //   }
  //   return null;
  // };

  // const navBreadcrumbs = getNavBreadcrumbs();

  // Generate breadcrumbs from path
  const pathSegments = location.pathname.split("/").filter(Boolean);
  const pathBreadcrumbs = pathSegments.flatMap((segment, index) => {
    const path = "/" + pathSegments.slice(0, index + 1).join("/");
    const isId = /^\d+$/.test(segment);
    const parentSegment = isId ? pathSegments[index - 1] : null;

    // Skip bare IDs when the parent segment already names the page (e.g. batch-details/:id)
    if (isId && (!parentSegment || !detailLabelMap[parentSegment])) {
      return [];
    }

    const label =
      isId && parentSegment && detailLabelMap[parentSegment]
        ? detailLabelMap[parentSegment]
        : getPathSegmentLabel(segment);
    return [{ label, path, showKitchenIcon: false }];
  });

  const breadcrumbs = [
    {
      label: getContextLabel(),
      path: undefined,
      showKitchenIcon: !user?.branchName,
    },
    ...pathBreadcrumbs,
  ];

  return (
    <header
      className={cn(
        "sticky top-0 z-30 flex md:items-center  items-start md:justify-between justify-center flex-col md:flex-row",
        "h-16 px-6 bg-white border-b border-grey-100",
        "transition-all duration-300",
        isMobile && "px-16 h-20 gap-2"
      )}
    >
      {/* Left side - Breadcrumbs */}
      <div className="flex items-center gap-2 text-sm">
        {breadcrumbs.length > 0 && !isMobile && (
          <>
            {breadcrumbs.map((crumb, index) => (
              <div
                key={`${crumb.label}-${index}`}
                className="flex items-center gap-2"
              >
                {index > 0 && (
                  <ChevronRight className="h-4 w-4 text-grey-500 rtl:rotate-180" />
                )}
                <div className="flex items-center gap-1.5">
                  {crumb.showKitchenIcon && (
                    <IconCentralKitchen size={14} color="#070606" />
                  )}
                  {index === breadcrumbs.length - 1 ? (
                    <span className="font-medium text-grey-900">
                      {crumb.label}
                    </span>
                  ) : crumb.path ? (
                    <Link
                      to={crumb.path}
                      className="text-grey-500 transition-colors hover:text-grey-900"
                    >
                      {crumb.label}
                    </Link>
                  ) : (
                    <span className="font-medium text-grey-900">
                      {crumb.label}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Right side - Actions */}
      <div className="flex items-center gap-3">
        {/* Currency Selector */}
        <CurrencySelector />

        {/* Language Switcher */}
        <LanguageSwitcher />

        {/* Notifications */}
        <button
          className={cn(
            "relative p-2 rounded-full",
            "text-grey-500 hover:text-grey-900",
            "hover:bg-green-50 transition-colors"
          )}
          aria-label={t("settings.notifications")}
        >
          <Bell className="w-5 h-5" />
          {/* Notification badge */}
          <span className="absolute top-1 end-1 w-2 h-2 bg-error-700 rounded-full" />
        </button>
      </div>
    </header>
  );
}
